import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TextField } from '@material-ui/core';
import SVG from 'react-inlinesvg';
import { toAbsoluteUrl } from '../../_metronic/_helpers';
import { useOutsideClick } from '../hooks/useOutsideClick';

const HOUR_IN_MS = 3600 * 1000;
export const DAY_IN_MS = HOUR_IN_MS * 24;
const WEEK_IN_MS = DAY_IN_MS * 7;
const MONTH_IN_MS = DAY_IN_MS * 30;

function formatDateToLocalString(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  const formattedString = `${year}-${month}-${day}T${hours}:${minutes}`;

  return formattedString;
}

export const DatePicker = forwardRef(
  ({ intl, onDateSelected, classes = '' }, ref) => {
    const [selected, setSelected] = useState('1d');
    const [initialDate, setInitialDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showInputs, setShowInputs] = useState(false);
    const containerRef = useOutsideClick(() => {
      setShowInputs(false);
    });

    useImperativeHandle(
      ref,
      () => {
        return {
          getCurrentFilter: () => {
            if (initialDate && endDate) {
              return {
                initialDate,
                endDate,
              };
            }
            const currentDate = new Date();
            const dates = {
              '1h': {
                initialDate: new Date(Date.now() - HOUR_IN_MS),
                endDate: currentDate,
              },
              '1d': {
                initialDate: new Date(Date.now() - DAY_IN_MS),
                endDate: currentDate,
              },
              '1w': {
                initialDate: new Date(Date.now() - WEEK_IN_MS),
                endDate: currentDate,
              },
              '1m': {
                initialDate: new Date(Date.now() - MONTH_IN_MS),
                endDate: currentDate,
              },
            };
            if (selected === 'custom') {
              setSelected('1d');
              return dates['1d'];
            }
            return dates[selected];
          },
        };
      },
      [selected, initialDate, endDate]
    );

    const handleSelection = type => () => {
      setSelected(type);
      if (type === 'custom') {
        if (selected !== 'custom') {
          const initialDate = new Date();
          initialDate.setHours(0, 0, 0, 0);
          const endDate = new Date();
          setInitialDate(initialDate);
          setEndDate(endDate);
        }
        setShowInputs(val => !val);
        return;
      }
      setShowInputs(false);
      const currentDate = new Date();
      const dates = {
        '1h': {
          initialDate: new Date(Date.now() - HOUR_IN_MS),
          endDate: currentDate,
        },
        '1d': {
          initialDate: new Date(Date.now() - DAY_IN_MS),
          endDate: currentDate,
        },
        '1w': {
          initialDate: new Date(Date.now() - WEEK_IN_MS),
          endDate: currentDate,
        },
        '1m': {
          initialDate: new Date(Date.now() - MONTH_IN_MS),
          endDate: currentDate,
        },
      };
      onDateSelected(dates[type].initialDate, dates[type].endDate);
    };

    const handleDate = type => e => {
      const { value } = e.target;
      if (value) {
        type === 'start'
          ? setInitialDate(new Date(value))
          : setEndDate(new Date(value));
      } else {
        type === 'start' ? setInitialDate(null) : setEndDate(null);
      }
    };

    const filter = () => {
      if (!initialDate || !endDate) return;
      if (initialDate?.getDate() > endDate?.getDate()) return;
      setShowInputs(false);
      onDateSelected(initialDate, endDate);
    };

    return (
      <div className={classes}>
        <div
          style={{
            borderRadius: 5,
            border: '1px solid gray',
            width: 'fit-content',
          }}
          className="bg-white position-relative"
        >
          <button
            type="button"
            style={{ borderTopLeftRadius: 5, borderBottomLeftRadius: 5 }}
            onClick={handleSelection('1h')}
            className={` ${
              selected === '1h' ? 'active' : ''
            } date-button-border px-3 py-1 date-preset-btn reset-css`}
          >
            1H
          </button>
          <button
            type="button"
            onClick={handleSelection('1d')}
            className={`${
              selected === '1d' ? 'active' : ''
            } date-button-border px-3 py-1 date-preset-btn reset-css`}
          >
            1D
          </button>
          <button
            type="button"
            onClick={handleSelection('1w')}
            className={`${
              selected === '1w' ? 'active' : ''
            } date-button-border px-3 py-1 date-preset-btn reset-css`}
          >
            1W
          </button>
          <button
            type="button"
            onClick={handleSelection('1m')}
            className={`${
              selected === '1m' ? 'active' : ''
            } date-button-border px-3 py-1 date-preset-btn reset-css`}
          >
            1M
          </button>
          <button
            type="button"
            style={{ borderTopRightRadius: 5, borderBottomRightRadius: 5 }}
            onClick={handleSelection('custom')}
            className={` ${
              selected === 'custom' ? 'active' : ''
            } date-button-border px-3 py-1 date-preset-btn reset-css`}
          >
            Custom
          </button>
        </div>
        <AnimatePresence exitBeforeEnter>
          {selected === 'custom' && showInputs && (
            <>
              <motion.div
                className="p-3 overflow-hidden mt-1 bg-white position-absolute"
                initial={{
                  height: 0,
                }}
                animate={{
                  height: 'auto',
                }}
                exit={{
                  height: 0,
                }}
                transition={{
                  duration: 0.2,
                }}
                style={{
                  border: '1px solid gray',
                  borderRadius: 4,
                  width: 'fit-content',
                }}
                ref={containerRef}
              >
                <div className="d-flex">
                  <TextField
                    label={intl.formatMessage({ id: 'START_DATE' })}
                    type="datetime-local"
                    onChange={handleDate('start')}
                    value={
                      initialDate ? formatDateToLocalString(initialDate) : ''
                    }
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        fontSize: '1.4rem',
                      },
                    }}
                  />
                  <TextField
                    label={intl.formatMessage({ id: 'END_DATE' })}
                    type="datetime-local"
                    onChange={handleDate('end')}
                    value={endDate ? formatDateToLocalString(endDate) : ''}
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        fontSize: '1.4rem',
                      },
                    }}
                    className="ml-3"
                  />

                  <span
                    className="d-flex ml-3"
                    style={{ alignItems: 'flex-end' }}
                  >
                    <SVG
                      uniquifyIDs
                      src={toAbsoluteUrl('/media/svg/icons/Text/Filter.svg')}
                      className="cursor-pointer filter-btn"
                      style={{ borderRadius: 4 }}
                      onClick={filter}
                    />
                  </span>
                </div>
                {initialDate?.getDate() > endDate?.getDate() ? (
                  <div className="d-flex">
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {intl.formatMessage({ id: 'START_GREATER_THAN_END' })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }
);
